import { cn } from "@/lib/cn";

/**
 * Form field wrapper — label + control + optional helper / error text.
 *
 * Operator forms (NewIssue, EditIssue, settings tabs) used to repeat the
 * same `<label class="text-[11px] uppercase…"><Input…/></label>` block on
 * every input. `Field` consolidates that so a row of fields lines up the
 * same way regardless of which form it lives in.
 *
 *   <Field label="Title" required>
 *     <Input value={…} onChange={…} />
 *   </Field>
 */
export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
  className,
}: {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-[12px] uppercase tracking-wide text-fg-dim font-medium"
        >
          {label}
          {required && <span aria-hidden className="text-status-critical ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-[12px] text-status-critical">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-fg-muted leading-relaxed">{hint}</p>
      ) : null}
    </div>
  );
}

/** Small uppercase label without a wrapping field — for in-pane sections. */
export function FieldLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("text-[12px] uppercase tracking-wide text-fg-dim font-medium", className)}>
      {children}
    </div>
  );
}
